import { MenuItem } from '../types/menu';

export type MenuItemTextField = 'name' | 'description' | 'disclaimer';

/**
 * Display text for a menu item field in a language.
 * Only uses the transcreated (friendly) translation, never the direct literal one.
 */
export function getMenuItemDisplayText(item: MenuItem, field: MenuItemTextField, currentLanguage: string): string {
  const sourceLanguage = item.props?.sourceLanguage || 'jp';

  // disclaimer has no top-level field — always read from translations
  if (field === 'disclaimer') {
    return item.props?.translations?.[currentLanguage]?.disclaimer?.text || '';
  }

  // SOURCE LANGUAGE: Prefer cleaned translation (※ notes removed) if available,
  // then fall back to item.description so admin edits still display correctly.
  if (currentLanguage === sourceLanguage) {
    const cleaned = item.props?.translations?.[currentLanguage]?.[field]?.text;
    if (cleaned !== undefined && cleaned !== null && String(cleaned).trim() !== '') {
      return String(cleaned);
    }
    const topLevel = item[field];
    if (topLevel !== undefined && topLevel !== null && String(topLevel).trim() !== '') {
      return String(topLevel);
    }
    return item[field] ?? '';
  }

  // PRIMARY: Try new translations structure first (transcreated friendly translation)
  const newTranslation = item.props?.translations?.[currentLanguage]?.[field]?.text;
  if (newTranslation) {
    return newTranslation;
  }

  // LEGACY: Fallback for English (prefer props.translations.en; then enLocalization)
  if (currentLanguage === 'en') {
    switch (field) {
      case 'name':
        return item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? item.name;
      case 'description':
        return item.props?.translations?.en?.description?.text ?? item.props?.enLocalization?.description ?? item.description;
    }
  }

  // Final fallback to raw data
  if (field === 'name' || field === 'description') {
    return item[field] || '';
  }
  return '';
}
